import React, { useEffect, useState } from 'react';
import { Leaf, Plus, Check, Sprout, Store, Gift } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RETAILERS = ['New World', "Pak'nSave", 'Woolworths', 'Coles', 'Aldi', 'Farmers Market', 'Local greengrocer', 'Other'];

export function PriceSightingsPanel({ user }) {
  const [stores, setStores] = useState([]);
  const [rewards, setRewards] = useState(null);
  const [refreshInfo, setRefreshInfo] = useState(null);
  const [crops, setCrops] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ crop_name: '', price: '', retailer: 'New World', region: (user?.location || '').split(',')[0].trim() });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [claiming, setClaiming] = useState(null);
  const [claimForm, setClaimForm] = useState({ name: user?.name || '', address: '', notes: '' });

  const load = async () => {
    try {
      const [s, r, rl] = await Promise.all([
        axios.get(`${API}/value/store-averages`, { withCredentials: true }),
        axios.get(`${API}/value/seed-rewards`, { withCredentials: true }),
        axios.get(`${API}/value/refresh-log?limit=1`, { withCredentials: true }),
      ]);
      setStores(s.data.stores);
      setRewards(r.data);
      setRefreshInfo(rl.data[0] || null);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    load();
    axios.get(`${API}/crops`, { withCredentials: true })
      .then(r => setCrops(r.data.map(c => c.name).sort()))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage(null);
    try {
      const r = await axios.post(`${API}/value/contribute-price`, {
        crop_name: form.crop_name.toLowerCase(),
        price: parseFloat(form.price),
        unit: 'kg',
        retailer: form.retailer,
        region: form.region,
      }, { withCredentials: true });
      const sr = r.data.seed_rewards;
      if (sr?.new_vouchers?.length > 0) {
        setMessage({ type: 'voucher', text: `You've earned a FREE seed pack! 🌱 Claim it below.` });
      } else {
        setMessage({ type: 'ok', text: `Thanks! +5 seed points — ${sr ? Math.ceil((sr.threshold - sr.points) / 5) : ''} more sightings to a free seed pack` });
      }
      setForm(f => ({ ...f, crop_name: '', price: '' }));
      setShowForm(false);
      load();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || "Couldn't save that sighting — check the price and try again." });
    } finally { setSubmitting(false); }
  };

  const claim = async (e) => {
    e.preventDefault();
    try {
      const r = await axios.post(`${API}/value/seed-rewards/claim`, { voucher_id: claiming, ...claimForm }, { withCredentials: true });
      setMessage({ type: 'ok', text: r.data.message });
      setClaiming(null);
      load();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Claim failed — try again.' });
    }
  };

  const earnedVoucher = rewards?.vouchers?.find(v => v.status === 'earned');
  const pct = rewards ? Math.min(100, (rewards.points / rewards.threshold) * 100) : 0;

  return (
    <div className="bg-[#FAF6EC] rounded-3xl p-5 border border-[#1A4331]/10 space-y-4" data-testid="price-sightings-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="font-bold tracking-wider uppercase text-sm text-[#1A4331] flex items-center gap-2">
            <Leaf className="w-4 h-4 text-[#6B9F4A]" /> Community price sightings
          </p>
          {refreshInfo && (
            <p className="text-[11px] text-[#7A6B57] mt-1" data-testid="official-refresh-info">
              Blended with {refreshInfo.items_updated} official NZ produce prices · updated {new Date(refreshInfo.ran_at).toLocaleDateString()}
            </p>
          )}
        </div>
        <button onClick={() => setShowForm(!showForm)} data-testid="add-sighting-button"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#FAF6EC] bg-[#1A4331] hover:bg-[#0F2D1F] py-2 px-4 rounded-full transition-colors">
          <Plus className="w-4 h-4" /> {showForm ? 'Cancel' : 'Add a sighting'}
        </button>
      </div>

      {/* Seed pack rewards progress */}
      {rewards && (
        <div className="bg-[#6B9F4A]/10 border border-[#6B9F4A]/25 rounded-2xl p-4" data-testid="seed-rewards-card">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm font-semibold text-[#1A4331] flex items-center gap-2">
              <Sprout className="w-4 h-4 text-[#6B9F4A]" />
              {rewards.points} / {rewards.threshold} seed points
            </p>
            <p className="text-xs text-[#7A6B57]" data-testid="seed-rewards-progress-text">
              {earnedVoucher ? 'Free seed pack earned!' : `${rewards.sightings_to_next_pack} more sighting${rewards.sightings_to_next_pack === 1 ? '' : 's'} to a free seed pack 🌱`}
            </p>
          </div>
          <div className="h-2 bg-white rounded-full overflow-hidden mt-2">
            <div className="h-full bg-gradient-to-r from-[#6B9F4A] to-[#C9A85B] transition-all duration-700" style={{ width: `${pct}%` }}></div>
          </div>
          {earnedVoucher && claiming !== earnedVoucher.id && (
            <button onClick={() => setClaiming(earnedVoucher.id)} data-testid="claim-seed-pack-button"
              className="mt-3 w-full bg-[#6B9F4A] hover:bg-[#5A8A3D] text-white font-semibold py-2.5 rounded-full text-sm flex items-center justify-center gap-2">
              <Gift className="w-4 h-4" /> Claim your free seed pack ({earnedVoucher.code})
            </button>
          )}
          {claiming && (
            <form onSubmit={claim} className="mt-3 space-y-2" data-testid="claim-form">
              <input required placeholder="Your name" value={claimForm.name} data-testid="claim-name-input"
                onChange={e => setClaimForm({ ...claimForm, name: e.target.value })}
                className="w-full px-4 py-2.5 bg-white rounded-xl border border-[#1A4331]/10 outline-none focus:border-[#1A4331] text-sm" />
              <textarea required placeholder="Delivery address" value={claimForm.address} rows={2} data-testid="claim-address-input"
                onChange={e => setClaimForm({ ...claimForm, address: e.target.value })}
                className="w-full px-4 py-2.5 bg-white rounded-xl border border-[#1A4331]/10 outline-none focus:border-[#1A4331] text-sm resize-none" />
              <button type="submit" data-testid="claim-submit-button"
                className="w-full bg-[#1A4331] hover:bg-[#0F2D1F] text-[#FAF6EC] font-semibold py-2.5 rounded-full text-sm">
                Send me my seeds
              </button>
            </form>
          )}
          {rewards.packs_earned > 0 && !earnedVoucher && !claiming && (
            <p className="text-[11px] text-[#7A6B57] mt-2">{rewards.packs_earned} pack{rewards.packs_earned === 1 ? '' : 's'} earned so far — claimed packs are posted to you.</p>
          )}
        </div>
      )}

      {message && (
        <p data-testid="sighting-message" className={`text-sm font-medium flex items-center gap-1.5 ${message.type === 'error' ? 'text-[#C66B4A]' : 'text-[#6B9F4A]'}`}>
          {message.type !== 'error' && <Check className="w-4 h-4" />} {message.text}
        </p>
      )}

      {/* Add sighting form */}
      {showForm && (
        <form onSubmit={submit} className="bg-white/70 rounded-2xl p-4 grid sm:grid-cols-2 gap-3" data-testid="sighting-form">
          <div>
            <label className="block text-xs font-semibold text-[#2D241E] mb-1.5">Crop</label>
            <select required value={form.crop_name} onChange={e => setForm({ ...form, crop_name: e.target.value })} data-testid="sighting-crop-select"
              className="w-full px-4 py-2.5 bg-white rounded-xl border border-[#1A4331]/10 outline-none focus:border-[#1A4331] text-sm">
              <option value="">Choose a crop…</option>
              {crops.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#2D241E] mb-1.5">Price per kg ($)</label>
            <input type="number" step="0.01" min="0.1" required value={form.price} data-testid="sighting-price-input"
              onChange={e => setForm({ ...form, price: e.target.value })} placeholder="e.g. 8.99"
              className="w-full px-4 py-2.5 bg-white rounded-xl border border-[#1A4331]/10 outline-none focus:border-[#1A4331] text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#2D241E] mb-1.5">Supermarket / store</label>
            <select value={form.retailer} onChange={e => setForm({ ...form, retailer: e.target.value })} data-testid="sighting-retailer-select"
              className="w-full px-4 py-2.5 bg-white rounded-xl border border-[#1A4331]/10 outline-none focus:border-[#1A4331] text-sm">
              {RETAILERS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#2D241E] mb-1.5">Region</label>
            <input required value={form.region} onChange={e => setForm({ ...form, region: e.target.value })} data-testid="sighting-region-input"
              placeholder="e.g. Auckland"
              className="w-full px-4 py-2.5 bg-white rounded-xl border border-[#1A4331]/10 outline-none focus:border-[#1A4331] text-sm" />
          </div>
          <button type="submit" disabled={submitting} data-testid="sighting-submit-button"
            className="sm:col-span-2 bg-[#1A4331] hover:bg-[#0F2D1F] text-[#FAF6EC] font-semibold py-3 rounded-full text-sm disabled:opacity-50">
            {submitting ? 'Sharing…' : 'Share sighting · +5 seed points'}
          </button>
        </form>
      )}

      {/* Store averages */}
      {stores.length === 0 ? (
        <p className="text-sm text-[#7A6B57] italic">No sightings yet — be the first to add one and start earning seed points.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-3" data-testid="store-averages">
          {stores.map(s => (
            <div key={`${s.retailer}-${s.region}`} className="bg-white/70 rounded-2xl p-4" data-testid={`store-${s.retailer.replace(/[^a-zA-Z]/g, '')}-${s.region.replace(/[^a-zA-Z]/g, '')}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-[#1A4331] flex items-center gap-1.5">
                  <Store className="w-4 h-4 text-[#C9A85B]" /> {s.retailer} · {s.region}
                </p>
                <span className="text-[10px] text-[#7A6B57]">{s.total_sightings} sighting{s.total_sightings === 1 ? '' : 's'}</span>
              </div>
              <div className="space-y-1">
                {s.crops.slice(0, 5).map(c => (
                  <div key={c.crop_name} className="flex items-center justify-between text-sm">
                    <span className="text-[#2A3528] capitalize">{c.crop_name}</span>
                    <span className="font-semibold text-[#1A4331]">${c.avg_price}/kg <span className="text-[10px] text-[#7A6B57] font-normal">avg of {c.count}</span></span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
